import { SearchInputTypes, useLoading } from '@genial/components';
import { useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { useEffect, useState } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';

import { useRecoilQuery } from '../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../routes/types';
import { useBatchTicketActions } from '../../../store/batchTicket/actions';
import {
  basketAdvisorOrderIdState,
  batchTicketCustomerFilterState,
  customerCartStatusState,
  productsCartStatusState,
  tagsState,
} from '../../../store/batchTicket/atom';
import {
  customerCartCountSelector,
  customersQuery,
  GetAllTagsByAdvisorSelector,
} from '../../../store/batchTicket/selectors';
import { masterStateSelectedAdvisorSelector } from '../../../store/master/selector';
import { CustomerItemDataProps, orderByProps } from '../utils/types';

export const useCustomerSection = () => {
  const {
    cleanCustomersCart,
    onChangeCustomerCartStatus,
    onChangeProductsCartStatus,
    addTagsByAdvisor,
  } = useBatchTicketActions();
  const { hide, show } = useLoading();

  const customerCartStatus = useRecoilValue(customerCartStatusState);
  const sectionTitle =
    customerCartStatus === 'complete' ? 'Clientes adicionados' : 'Selecione os clientes';

  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const tagList = useRecoilValue(tagsState).list;
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const customerCartCount = useRecoilValue(customerCartCountSelector);
  const batchTicketCustomerFilter = useRecoilValue(batchTicketCustomerFilterState);
  const filterCount = batchTicketCustomerFilter.appliedFilters.length;
  const appliedFilter = batchTicketCustomerFilter.appliedFilters;
  const basketAdvisorOrderId = useRecoilValue(basketAdvisorOrderIdState);
  const skeletonItemCount = useBreakpointValue({ base: 4, sm: 4, lg: 6 });

  const resetCustomerCartStatus = useResetRecoilState(customerCartStatusState);
  const resetProductsCartStatus = useResetRecoilState(productsCartStatusState);

  const [allCustomers, setAllCustomers] = useState<CustomerItemDataProps[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState<orderByProps>({
    value: 'name',
    direction: 'desc',
  });

  const tagsFilter = appliedFilter.find((filter) => filter.fieldName === 'tags');

  const {
    data: response,
    error,
    loading: isLoading,
    refresher,
  } = useRecoilQuery(customersQuery({ page, searchTerm, tags: tagsFilter?.filter as string[] }), {
    cachePolicy: 'no-cache',
  });

  const {
    data: responseTags,
    error: errorTags,
    loading: isLoadingTags,
    refresher: refresherTags,
  } = useRecoilQuery(GetAllTagsByAdvisorSelector({ advisorId: undefined }), {
    cachePolicy: 'no-cache',
  });

  const orderButtonIsDisabled = true;
  const filterButtonIsDisabled = tagList.length === 0;
  const clearSelectionButtonIsDisabled = customerCartCount === 0;
  const isFirstPage = page === 1;

  const handleEndReached = () => {
    if (!response?.meta?.pagination.hasNext || isLoading || error) {
      return;
    }
    setPage((prev) => prev + 1);
  };

  const handlePressOrderButton = () => {};

  const handlePressFilterButton = () => {
    navigate('BatchTicketCustomerModalFilter');
  };

  const handlePressBasketButton = () => {
    navigate('BatchTicketCart', { key: 'addedCustomers' });
  };

  const handleClearSearchTerm = () => {
    setSearchTerm('');
    setPage(1);
  };

  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term);
    setAllCustomers([]);
    setPage(1);
  };

  const handleOrderBy = (value?: string) => {
    if (!value) {
      return;
    }

    setOrderBy((currentData) => {
      if (currentData.value !== value) {
        return { value, direction: 'desc' };
      }

      const direction = currentData.direction === 'asc' ? 'desc' : 'asc';

      return { value, direction };
    });
  };

  const searchInputProps: SearchInputTypes.Props = {
    hStackProps: { mb: 0 },
    onClear: handleClearSearchTerm,
    onSearch: handleSetSearchTerm,
    isLoading,
    placeholder: 'Pesquise por nome, conta ou CPF',
  };

  useEffect(() => {
    if (!responseTags?.list) {
      return;
    }

    if (page === 1) {
      addTagsByAdvisor(responseTags);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [responseTags?.list]);

  useEffect(() => {
    if (!response?.data) {
      return;
    }

    if (page === 1) {
      setAllCustomers(response.data);
    } else {
      setAllCustomers((prev) => [...prev, ...response.data]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response?.data]);

  useEffect(() => {
    setAllCustomers([]);
    setPage(1);
  }, [selectedAdvisor]);

  useEffect(() => {
    if (appliedFilter.length > 0) {
      setAllCustomers([]);
      setPage(1);
    }
  }, [appliedFilter]);

  const handlePressClearSectionButton = async () => {
    show({
      title: 'Aguarde estamos limpando sua cesta.',
      description: 'Este processo pode levar alguns segundos, não saia desta pagina.',
    });
    await cleanCustomersCart(basketAdvisorOrderId);
    if (customerCartStatus === 'complete') {
      resetCustomerCartStatus();
      resetProductsCartStatus();
    }
    hide();
  };

  const handleAddCustomers = () => {
    onChangeCustomerCartStatus('start');
    onChangeProductsCartStatus('locked');
  };

  return {
    itemCount: customerCartCount,
    filterCount,
    orderButtonIsDisabled,
    filterButtonIsDisabled,
    clearSelectionButtonIsDisabled,
    searchInputProps,
    orderBy,
    response: { meta: response?.meta, data: allCustomers },
    error,
    isLoading: isLoading && isFirstPage,
    isLoadingMore: isLoading && !isFirstPage,
    onEndReached: handleEndReached,
    handleOrderBy,
    refresher,
    handlePressClearSectionButton,
    handlePressFilterButton,
    handlePressOrderButton,
    handlePressBasketButton,
    skeletonItemCount,
    customerCartStatus,
    sectionTitle,
    handleAddCustomers,
    errorTags,
    isLoadingTags,
    refresherTags,
  };
};
